import { useEffect, useState } from "react";
import { api } from "../lib/api";

type Customer = {
  id: number;
  name: string | null;
  phone_number: string;
  current_punches: number;
  total_punches: number;
  reward_available: number;
  last_event_at: string | null;
};

function maskPhone(p: string) {
  if (!p) return "—";
  const tail = p.slice(-4);
  return `${p.slice(0, 3)} ••• ${tail}`;
}

export function Customers() {
  const [rows, setRows] = useState<Customer[]>([]);
  const [sort, setSort] = useState<"recent" | "total" | "current">("recent");
  const [err, setErr] = useState<string | null>(null);
  const [showFull, setShowFull] = useState<Record<number, boolean>>({});

  useEffect(() => {
    setRows([]);
    api<{ data: Customer[] }>(`/api/customers?sort=${sort}`)
      .then((r) => setRows(r.data))
      .catch((e: Error) => setErr(e.message));
  }, [sort]);

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h1 className="text-xl font-semibold">Customers</h1>
        <div className="flex gap-2 text-sm">
          {(["recent", "total", "current"] as const).map((s) => (
            <button
              key={s}
              onClick={() => setSort(s)}
              className={`px-2 py-1 rounded ${sort === s ? "bg-punchy-500 text-white" : "bg-neutral-200"}`}
            >
              {s === "recent" ? "Recent" : s === "total" ? "Top lifetime" : "Most punches now"}
            </button>
          ))}
        </div>
      </div>
      {err && <div className="text-red-600 text-sm mb-3">{err}</div>}
      <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase text-neutral-500 bg-neutral-50">
            <tr>
              <th className="text-left px-3 py-2">Name</th>
              <th className="text-left px-3 py-2">Phone</th>
              <th className="text-right px-3 py-2">Now</th>
              <th className="text-right px-3 py-2">Lifetime</th>
              <th className="text-left px-3 py-2">Last event</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={5} className="text-center text-neutral-500 px-3 py-6">No customers yet.</td></tr>
            )}
            {rows.map((c) => (
              <tr key={c.id} className="border-t border-neutral-100">
                <td className="px-3 py-2">{c.name ?? <span className="text-neutral-400 italic">no name</span>}</td>
                <td className="px-3 py-2 font-mono text-xs">
                  <button onClick={() => setShowFull((s) => ({ ...s, [c.id]: !s[c.id] }))}>
                    {showFull[c.id] ? c.phone_number : maskPhone(c.phone_number)}
                  </button>
                </td>
                <td className="px-3 py-2 text-right">
                  {c.current_punches}{Number(c.reward_available) ? " 🎁" : ""}
                </td>
                <td className="px-3 py-2 text-right">{c.total_punches}</td>
                <td className="px-3 py-2 text-neutral-500">{c.last_event_at ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
